// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { CheckCircle, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { InlineSpinner } from "@/components/shared/inline-spinner";
import { ProgressBar } from "@/components/shared/progress-bar";
import { cn } from "@/lib/utils";

/**
 * Whether a file satisfies a comma-separated `accept` list. Mirrors the browse picker's `accept`
 * attribute so drag-and-drop enforces the same restriction. Supports extension patterns (".bin"),
 * wildcard MIME ("image/*"), and exact MIME types; no list means anything is allowed.
 */
function isFileAccepted(file: File, accept?: string): boolean {
  const patterns = (accept ?? "")
    .split(",")
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);
  if (patterns.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return patterns.some((p) => {
    if (p.startsWith(".")) return name.endsWith(p);
    if (p.endsWith("/*")) return type.startsWith(p.slice(0, -1));
    return type === p;
  });
}

interface FileDropZoneProps {
  /** Comma-separated accept list, e.g. ".bin,.txz". */
  accept?: string;
  onSelect: (file: File) => void;
  /** The currently selected / uploaded file, shown in the success state. */
  selected?: { name: string; size: number };
  /** Upload progress 0..1. When provided, the uploading/processing state is shown. */
  progress?: number;
  /** Idle-state primary text. */
  title: string;
  /** Idle-state secondary text. */
  subtitle?: string;
  uploadingLabel?: (percent: number) => string;
  processingLabel?: string;
  invalid?: boolean;
  disabled?: boolean;
  className?: string;
}

/** Dashed drag-and-drop / click-to-select file zone (shared by the import & OTA upload flows). */
export function FileDropZone({
  accept,
  onSelect,
  selected,
  progress,
  title,
  subtitle,
  uploadingLabel,
  processingLabel,
  invalid,
  disabled,
  className,
}: FileDropZoneProps) {
  const { t } = useTranslation("back-office");
  const isUploading = progress !== undefined;
  const progressPct = Math.round((progress ?? 0) * 100);
  const fileRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragOver(false);
    if (disabled) return;
    const file = e.dataTransfer.files[0];
    if (!file) return;
    // The browse picker enforces `accept` natively; the drop path must check it too, else a
    // disallowed file type would be silently accepted (CIN-670). Decline it with a toast.
    if (!isFileAccepted(file, accept)) {
      toast.error(
        t("common.fileTypeNotAccepted", {
          types: (accept ?? "")
            .split(",")
            .map((p) => p.trim())
            .filter(Boolean)
            .join(", "),
        }),
      );
      return;
    }
    onSelect(file);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) onSelect(file);
  }

  return (
    <div
      className={cn(
        "rounded-lg border-2 border-dashed p-4 text-center transition-colors flex items-center justify-center",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
        !selected && "h-[6.5rem]",
        isDragOver
          ? "border-primary bg-primary/5"
          : selected
            ? "border-green-300 bg-green-50 dark:border-green-900 dark:bg-green-950"
            : invalid
              ? "border-destructive"
              : "border-muted-foreground/25 hover:border-muted-foreground/50",
        className,
      )}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
      onClick={() => !disabled && fileRef.current?.click()}
    >
      <input
        ref={fileRef}
        type="file"
        className="hidden"
        accept={accept}
        disabled={disabled}
        onChange={handleFileChange}
      />
      {selected ? (
        <div className="flex max-w-full items-center justify-center gap-3">
          <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
          <span className="min-w-0 break-words text-sm font-medium">{selected.name}</span>
          <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
            ({Math.round(selected.size / 1024)} KB)
          </span>
        </div>
      ) : isUploading ? (
        <div className="flex w-full flex-col items-center justify-center gap-2">
          <InlineSpinner label={progressPct < 100 ? uploadingLabel?.(progressPct) : processingLabel} className="py-0" />
          <ProgressBar value={progress ?? 0} className="max-w-[12rem]" />
        </div>
      ) : (
        <div>
          <Upload className="mx-auto h-6 w-6 text-muted-foreground mb-1" />
          <p className="text-sm font-medium">{title}</p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
      )}
    </div>
  );
}
